import { wilson } from "./learner";
import { SCOREBOARD_FLOORS } from "./scoreboard";
import type { InquiryType, Outcome } from "./types";

/**
 * The funnel ledger (build list B1, revenue 2026-10-11). PURE: no clock, no IO.
 *
 * ONE SOURCE OF TRUTH (CLAUDE.md section 5). Nothing here is stored. A thread's
 * timeline is derived from facts that already exist (messages, `consult.proposed`,
 * `appointment.*`, `outcome.*`) plus the one new kind B3 writes, `contact.captured`.
 * Replaying the same facts gives the same funnel, and input order never matters:
 * every derivation sorts or takes a minimum.
 *
 * WHAT COUNTS AS A REPLY (F-06). `outbound` is a message that LEFT the building
 * (an owner-typed `shop` message, or an `agent` message transmit.ts writes only on
 * a definite transmit). A draft that was gate-passed, approved or sent but not
 * transmitted is not a reply and must never be passed in. A reply is an outbound at
 * or after the first inbound.
 *
 * WHAT COUNTS AS REACHED. A stage is reached when its own fact exists. Reaching a
 * later stage counts as having passed every earlier one for the COUNTS (a sale the
 * owner closed by phone with no recorded reply still entered the funnel), while the
 * per-stage first-reached time stays null for a stage with no fact of its own, so
 * a median is never built on an invented time.
 *
 * OUTCOMES. Only a STANDING outcome counts: `reversedAt` set (an owner's
 * `outcome.reversed`, or the ghost labeler's own `outcome.ghost_reversed` stamp)
 * removes it entirely. `deposit` and `sold` are stages; `booked` is read as
 * `consult_booked` (the owner's own tap that a consult is booked); `ongoing`
 * settles nothing and is ignored; `ghosted` and `lost` are terminals.
 *
 * TERMINALS. A terminal (`no_show`, `lost`, `ghosted`) is reported only while
 * nothing happened after it: it is cleared by any stage reached at or after its
 * time, and a sale always ends the thread as sold. `no_show` is an appointment's
 * no-show with no attended appointment at or after it. The latest standing
 * terminal wins.
 *
 * The minimum sample is the scoreboard's rule, not a new number.
 */

export const FUNNEL_STAGES = [
  "first_inbound",
  "first_reply",
  "contact_captured",
  "consult_proposed",
  "consult_booked",
  "consult_attended",
  "deposit",
  "sold",
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export const FUNNEL_TERMINALS = ["no_show", "lost", "ghosted"] as const;
export type FunnelTerminal = (typeof FUNNEL_TERMINALS)[number];

/**
 * The fewest threads (the denominator of a step, or the number of measured gaps) before a rate or median is
 * read. Reuses the scoreboard's floor for a plain per-thread rate (`repairRate`, 20) rather than minting a
 * number. AWAITING RATIFICATION, inherited from the scoreboard's markers.
 */
export const FUNNEL_MIN_SAMPLE: number = SCOREBOARD_FLOORS.repairRate; // AWAITING RATIFICATION

/** `contact.captured`'s shape for funnel purposes (B3 writes it): the kind and when, never the value. */
export interface FunnelContactCapture {
  readonly kind: "phone" | "email";
  readonly at: Date;
}

export interface FunnelAppointmentFact {
  readonly bookedAt: Date;
  readonly attendedAt: Date | null;
  readonly noShowAt: Date | null;
}

export interface FunnelOutcomeFact {
  readonly label: Outcome;
  /** `recorded_at`. */
  readonly at: Date;
  /** Non-null when the outcome no longer stands (owner reversal, or a ghost reversed by the customer returning). */
  readonly reversedAt: Date | null;
  readonly inquiryType: InquiryType | null;
  /** B2 fills this later; null is "unbanded". An analytic bucket only, never shown to a customer. */
  readonly priceBand: string | null;
}

export interface FunnelThreadFacts {
  readonly threadId: string;
  /** Customer message times. */
  readonly inbound: readonly Date[];
  /** Times a shop or agent message LEFT (F-06 transmitted), never a draft. */
  readonly outbound: readonly Date[];
  readonly contactCaptured: readonly FunnelContactCapture[];
  /** `consult.proposed` times. */
  readonly consultProposed: readonly Date[];
  readonly appointments: readonly FunnelAppointmentFact[];
  readonly outcomes: readonly FunnelOutcomeFact[];
}

export interface FunnelTimeline {
  readonly threadId: string;
  readonly reachedAt: Readonly<Record<FunnelStage, Date | null>>;
  readonly highest: FunnelStage | null;
  readonly terminal: FunnelTerminal | null;
  /** The type on the latest standing outcome that carries one, else null. */
  readonly inquiryType: InquiryType | null;
  /** The band on the latest standing deposit or sold outcome that carries one, else null. */
  readonly priceBand: string | null;
}

function earliest(dates: readonly Date[]): Date | null {
  let best: Date | null = null;
  for (const d of dates) if (best === null || d.getTime() < best.getTime()) best = d;
  return best;
}

function latest<T>(items: readonly T[], at: (x: T) => number): T | null {
  let best: T | null = null;
  for (const x of items) if (best === null || at(x) > at(best)) best = x;
  return best;
}

/**
 * Every time each stage's OWN fact occurred on the thread, standing outcomes only, in no promised order. The one
 * spelling of "which fact is which stage": `deriveFunnelTimeline`'s `reachedAt` is the earliest of each list, so
 * the funnel report is unchanged. A windowed reader (B5's experiment analysis, fix round 1 S3) needs every time,
 * not the earliest: a thread that booked last spring and books again inside an experiment's window did book inside
 * the window, which the earliest-ever time cannot show.
 */
export function funnelStageEventTimes(f: FunnelThreadFacts): Readonly<Record<FunnelStage, readonly Date[]>> {
  const standing = f.outcomes.filter((o) => o.reversedAt === null);
  const firstInbound = earliest(f.inbound);
  return {
    first_inbound: [...f.inbound],
    first_reply: firstInbound === null ? [] : f.outbound.filter((d) => d.getTime() >= firstInbound.getTime()),
    contact_captured: f.contactCaptured.map((c) => c.at),
    consult_proposed: [...f.consultProposed],
    consult_booked: [...f.appointments.map((a) => a.bookedAt), ...standing.filter((o) => o.label === "booked").map((o) => o.at)],
    consult_attended: f.appointments.flatMap((a) => (a.attendedAt === null ? [] : [a.attendedAt])),
    deposit: standing.filter((o) => o.label === "deposit").map((o) => o.at),
    sold: standing.filter((o) => o.label === "sold").map((o) => o.at),
  };
}

export function deriveFunnelTimeline(f: FunnelThreadFacts): FunnelTimeline {
  const standing = f.outcomes.filter((o) => o.reversedAt === null);
  const times = funnelStageEventTimes(f);
  const reachedAt = Object.fromEntries(FUNNEL_STAGES.map((s) => [s, earliest(times[s])])) as Record<FunnelStage, Date | null>;

  let highest: FunnelStage | null = null;
  for (const s of FUNNEL_STAGES) if (reachedAt[s] !== null) highest = s;

  // Terminal candidates, standing only; the latest wins, and only if nothing progressed at or after it.
  const attendedTimes = f.appointments.flatMap((a) => (a.attendedAt === null ? [] : [a.attendedAt.getTime()]));
  const candidates: { terminal: FunnelTerminal; at: number }[] = [];
  for (const o of standing) {
    if (o.label === "lost" || o.label === "ghosted") candidates.push({ terminal: o.label, at: o.at.getTime() });
  }
  for (const a of f.appointments) {
    if (a.noShowAt !== null && !attendedTimes.some((x) => x >= a.noShowAt!.getTime())) {
      candidates.push({ terminal: "no_show", at: a.noShowAt.getTime() });
    }
  }
  let terminal: FunnelTerminal | null = null;
  if (reachedAt.sold === null) {
    const last = latest(candidates, (c) => c.at);
    if (last !== null) {
      // Stages reached strictly after the terminal clear it; one at the same instant does not.
      const progressedAfter = FUNNEL_STAGES.some((s) => {
        const d = reachedAt[s];
        return d !== null && d.getTime() > last.at;
      });
      terminal = progressedAfter ? null : last.terminal;
    }
  }

  const typed = latest(
    standing.filter((o) => o.inquiryType !== null),
    (o) => o.at.getTime(),
  );
  const banded = latest(
    standing.filter((o) => (o.label === "deposit" || o.label === "sold") && o.priceBand !== null),
    (o) => o.at.getTime(),
  );

  return {
    threadId: f.threadId,
    reachedAt,
    highest,
    terminal,
    inquiryType: typed?.inquiryType ?? null,
    priceBand: banded?.priceBand ?? null,
  };
}

/** Wilson 95% interval for k of n, in whole basis points (0..10000), rounded half up. Refuses nonsense. */
export function wilson95Bp(k: number, n: number): readonly [number, number] {
  if (!Number.isSafeInteger(k) || !Number.isSafeInteger(n) || n <= 0 || k < 0 || k > n) {
    throw new Error(`wilson95Bp: ${k} of ${n} is not a count out of a positive total.`);
  }
  const [lo, hi] = wilson(k, n);
  return [Math.round(lo * 10_000), Math.round(hi * 10_000)];
}

export type NotMeasured = "not_measured";

export interface FunnelRate {
  readonly bp: number;
  readonly lowBp: number;
  readonly highBp: number;
}

export interface FunnelTransition {
  readonly from: FunnelStage;
  readonly to: FunnelStage;
  /** Threads that reached `from`. */
  readonly denominator: number;
  /** Threads that reached `to` (or later). */
  readonly numerator: number;
  readonly rate: FunnelRate | NotMeasured;
  /** Median ms between the two stages' own times, upper middle, over threads with both and a non-negative gap. */
  readonly medianMs: number | NotMeasured;
  /** How many threads contributed to the median. */
  readonly gapSamples: number;
}

export interface FunnelGroup {
  readonly key: string;
  readonly threads: number;
  readonly stageCounts: Readonly<Record<FunnelStage, number>>;
  readonly terminals: Readonly<Record<FunnelTerminal, number>>;
  readonly transitions: readonly FunnelTransition[];
  /** Gaps left out of every median because the later stage's time precedes the earlier one's. */
  readonly negativeGaps: number;
}

export interface FunnelReport {
  readonly minSample: number;
  readonly all: FunnelGroup;
  readonly byInquiryType: readonly FunnelGroup[];
  readonly byPriceBand: readonly FunnelGroup[];
}

export const FUNNEL_UNTYPED = "untyped";
export const FUNNEL_UNBANDED = "unbanded";

function buildGroup(key: string, timelines: readonly FunnelTimeline[]): FunnelGroup {
  const idx = (s: FunnelStage): number => FUNNEL_STAGES.indexOf(s);
  const reached = (tl: FunnelTimeline, s: FunnelStage): boolean => tl.highest !== null && idx(tl.highest) >= idx(s);

  const stageCounts = Object.fromEntries(
    FUNNEL_STAGES.map((s) => [s, timelines.filter((tl) => reached(tl, s)).length]),
  ) as Record<FunnelStage, number>;
  const terminals: Record<FunnelTerminal, number> = { no_show: 0, lost: 0, ghosted: 0 };
  for (const tl of timelines) if (tl.terminal !== null) terminals[tl.terminal] += 1;

  let negativeGaps = 0;
  const transitions: FunnelTransition[] = [];
  for (let i = 0; i + 1 < FUNNEL_STAGES.length; i += 1) {
    const from = FUNNEL_STAGES[i]!;
    const to = FUNNEL_STAGES[i + 1]!;
    const denominator = stageCounts[from];
    const numerator = stageCounts[to];
    const gaps: number[] = [];
    for (const tl of timelines) {
      const a = tl.reachedAt[from];
      const b = tl.reachedAt[to];
      if (a === null || b === null) continue;
      const gap = b.getTime() - a.getTime();
      if (gap < 0) negativeGaps += 1;
      else gaps.push(gap);
    }
    gaps.sort((x, y) => x - y);
    let rate: FunnelRate | NotMeasured = "not_measured";
    if (denominator >= FUNNEL_MIN_SAMPLE) {
      const [lowBp, highBp] = wilson95Bp(numerator, denominator);
      rate = { bp: Math.round((numerator * 10_000) / denominator), lowBp, highBp };
    }
    const medianMs: number | NotMeasured =
      gaps.length >= FUNNEL_MIN_SAMPLE ? gaps[Math.floor(gaps.length / 2)]! : "not_measured";
    transitions.push({ from, to, denominator, numerator, rate, medianMs, gapSamples: gaps.length });
  }
  return { key, threads: timelines.length, stageCounts, terminals, transitions, negativeGaps };
}

function split(
  timelines: readonly FunnelTimeline[],
  keyOf: (tl: FunnelTimeline) => string,
  lastKey: string,
): FunnelGroup[] {
  const buckets = new Map<string, FunnelTimeline[]>();
  for (const tl of timelines) {
    const k = keyOf(tl);
    const list = buckets.get(k);
    if (list === undefined) buckets.set(k, [tl]);
    else list.push(tl);
  }
  // Named keys alphabetical, the null bucket always last: stable whatever the input order.
  const keys = [...buckets.keys()].sort((a, b) => (a === lastKey ? 1 : b === lastKey ? -1 : a < b ? -1 : a > b ? 1 : 0));
  return keys.map((k) => buildGroup(k, buckets.get(k)!));
}

/** The funnel report, in code and never the model. Order of `threads` never changes the result. */
export function funnelReport(threads: readonly FunnelThreadFacts[]): FunnelReport {
  const seen = new Set<string>();
  for (const th of threads) {
    if (seen.has(th.threadId)) throw new Error(`funnelReport: duplicate thread id ${th.threadId}. One fact set per thread.`);
    seen.add(th.threadId);
  }
  const timelines = threads.map(deriveFunnelTimeline).sort((a, b) => (a.threadId < b.threadId ? -1 : a.threadId > b.threadId ? 1 : 0));
  return {
    minSample: FUNNEL_MIN_SAMPLE,
    all: buildGroup("all", timelines),
    byInquiryType: split(timelines, (tl) => tl.inquiryType ?? FUNNEL_UNTYPED, FUNNEL_UNTYPED),
    byPriceBand: split(timelines, (tl) => tl.priceBand ?? FUNNEL_UNBANDED, FUNNEL_UNBANDED),
  };
}
